import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { buildCsvBlob, downloadBlob } from 'utils/csv';
import * as css from "./style";

interface Props {
  data: unknown[],
  fileName?: string,
  separator?: string,
}

/**
 * Export csv component wrapper
 *
 * Serializes on click rather than on render. react-csv's <CSVLink> built the
 * entire CSV inside render() -- plus a Blob and a discarded data: URI from it,
 * with the object URL never revoked -- which meant several copies of a 100MB+
 * string in memory on every render of a large export.
 *
 * The imperative handle keeps the existing call site contract:
 * `ref.current['link'].click()`.
 *
 * @param {Array<string | string[])[]} data
 * @param {React.Ref} ref
 * @param {string} fileName
 * @param {string} separator
 */
const ExportCsv = forwardRef(({ data, fileName = "export-report.csv", separator = "," }: Props, ref) => {
  // Read through a ref so click() always serializes the latest data, even when
  // the caller clicks from an effect in the same commit that supplied it.
  const latest = useRef({ data, fileName, separator });
  latest.current = { data, fileName, separator };

  useImperativeHandle(ref, () => ({
    link: {
      click() {
        const current = latest.current;
        const rows = (current.data || []) as unknown[][];
        downloadBlob(
          buildCsvBlob(rows, { separator: current.separator, uFEFF: false }),
          current.fileName,
        );
      },
    },
  }), []);

  return <css.Csvlink />;
});

export default ExportCsv;
